import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';

export const SessionContext = createContext();

// Disable session timeout by setting to a very large value (100 years)
const SESSION_TIMEOUT = 100 * 365 * 24 * 60 * 60 * 1000;
// Set warning timeout to a large value as well
const WARNING_TIMEOUT = 100 * 365 * 24 * 60 * 60 * 1000;

export const SessionProvider = ({ children, navigate }) => {
  // Get kioskId - one-time setup from URL, then persists forever in localStorage
  // Only checked at startup, never expires until kiosk reboots (localStorage cleared)
  // Works with any route: /start?kioskId=1, /?kioskId=1, /home?kioskId=1, etc.
  const getKioskId = () => {
    if (typeof window === 'undefined') return null;
    
    // Priority 1: Check URL query parameter (only at startup - allows initial setup/override)
    // Works with any route: /start?kioskId=1, /?kioskId=1, etc.
    const params = new URLSearchParams(window.location.search);
    const urlKioskId = params.get('kioskId');
    if (urlKioskId) {
      const parsed = parseInt(urlKioskId, 10);
      if (!isNaN(parsed) && parsed > 0) {
        // Store in localStorage permanently (persists until kiosk reboots)
        try {
          localStorage.setItem('kiosk_kioskId', String(parsed));
          console.log(`[Session] ✅ kioskId ${parsed} configured from URL (${window.location.pathname}?kioskId=${parsed})`);
          console.log(`[Session] ✅ Saved permanently to localStorage - will persist across all sessions until kiosk reboots`);
        } catch (e) {
          console.warn('[Session] Failed to save kioskId to localStorage:', e);
        }
        return parsed;
      }
    }
    
    // Priority 2: Check localStorage (persists forever until kiosk reboots)
    try {
      const stored = localStorage.getItem('kiosk_kioskId');
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed > 0) {
          console.log(`[Session] ✅ kioskId ${parsed} loaded from localStorage (persistent - no need for URL parameter)`);
          return parsed;
        }
      }
    } catch (e) {
      console.warn('[Session] Failed to read kioskId from localStorage:', e);
    }
    
    return null;
  };

  // Initialize kioskId once at startup - it will never change during this session
  const initialKioskId = useMemo(() => getKioskId(), []); // Only runs once on mount

  const [session, setSession] = useState(() => {
    // Warn if kioskId is missing (for centralized setup)
    if (!initialKioskId && typeof window !== 'undefined') {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isLocalhost) {
        console.warn('[Session] kioskId not found in URL or localStorage. Some features may not work correctly.');
        console.warn('[Session] Expected URL format: http://<server-ip>:5001?kioskId=1');
        console.warn('[Session] Once configured, kioskId persists forever until kiosk reboots');
      }
    }
    
    return {
      user: null,
      cart: [],
      users: [], // Add users array to store recent users
      lastActivity: Date.now(),
      userSelectedFlag: false,
      currentOrderId: null, // Track the current order ID
      lastOrigin: null, // 'products' | 'kotilinga' - track where the flow started
      kioskId: initialKioskId, // Kiosk ID - set once at startup, persists forever in localStorage
    };
  });
  
  const [showInactiveWarning, setShowInactiveWarning] = useState(false);
  // Use the provided navigate function

  // Reset timer on any user activity
  const resetTimer = useCallback(() => {
    setSession(prev => ({
      ...prev,
      lastActivity: Date.now(),
    }));
    setShowInactiveWarning(false);
  }, []);

  // Handle manual session reset (Home button)
  const resetSession = useCallback((options = {}) => {
    const { skipNavigation = false } = options;
    
    // kioskId is permanent - always preserve it from initial setup (never changes)
    // It's stored in localStorage and persists forever until kiosk reboots
    const permanentKioskId = initialKioskId;
    
    setSession({
      user: null,
      cart: [],
      userSelectedFlag: false, // Reset userSelectedFlag on session reset
      lastActivity: Date.now(),
      kioskId: permanentKioskId, // Always preserve permanent kioskId (never expires)
    });
    
    if (!skipNavigation) {
      navigate('/');
    }
  }, [navigate, initialKioskId]);

  // Handle inactivity
  useEffect(() => {
    let warningTimer;
    let sessionResetTimer;

    const checkInactivity = () => {
      const now = Date.now();
      const timeElapsed = now - session.lastActivity;

      if (timeElapsed > SESSION_TIMEOUT && !showInactiveWarning) {
        setShowInactiveWarning(true);
        warningTimer = setTimeout(() => {
          resetSession();
        }, WARNING_TIMEOUT);
      }
    };

    const activityListener = () => resetTimer();

    // Set up event listeners for user activity
    window.addEventListener('mousemove', activityListener);
    window.addEventListener('keypress', activityListener);
    window.addEventListener('click', activityListener);
    window.addEventListener('scroll', activityListener);

    return () => {
      clearTimeout(warningTimer);
      clearTimeout(sessionResetTimer);
      window.removeEventListener('mousemove', activityListener);
      window.removeEventListener('keypress', activityListener);
      window.removeEventListener('click', activityListener);
      window.removeEventListener('scroll', activityListener);
    };
  }, [resetTimer]);

  // Update cart in session and optionally update devotee
  const updateCart = useCallback((cart, devotee = null) => {
    setSession(prev => {
      const updated = {
        ...prev,
        cart: Array.isArray(cart) ? [...cart] : [],
        lastActivity: Date.now(),
      };
      
      // If devotee is provided, update it in the session
      if (devotee) {
        updated.user = { ...devotee };
        updated.userSelectedFlag = true;
        
        // Add to users list if not already present
        const existingUserIndex = updated.users?.findIndex(u => u.id === devotee.id) ?? -1;
        
        if (existingUserIndex >= 0) {
          // Update existing user
          updated.users = [...updated.users];
          updated.users[existingUserIndex] = { ...devotee };
        } else {
          // Add new user to the beginning of the users array
          updated.users = [
            { ...devotee },
            ...(updated.users || []).slice(0, 49) // Keep only the 50 most recent users
          ];
        }
      }
      
      return updated;
    });
  }, []);

  // Update user in session
  const updateUser = useCallback((user) => {
    if (!user) return;
    
    setSession(prev => {
      const updated = {
        ...prev,
        user: { ...user },
        userSelectedFlag: true,
        lastActivity: Date.now(),
      };
      
      // Also ensure the user is in the users list
      if (user.id) {
        const existingUserIndex = updated.users?.findIndex(u => u.id === user.id) ?? -1;
        
        if (existingUserIndex >= 0) {
          updated.users = [...updated.users];
          updated.users[existingUserIndex] = { ...user };
        } else {
          updated.users = [
            { ...user },
            ...(updated.users || []).slice(0, 49)
          ];
        }
      }
      
      return updated;
    });
  }, []);
  
  // Update userSelectedFlag and optionally update devotee
  const setUserSelected = useCallback((value, devotee = null) => {
    setSession(prev => {
      const updated = {
        ...prev,
        userSelectedFlag: value,
        lastActivity: Date.now(),
      };
      
      // If devotee is provided, update it in the session
      if (devotee) {
        updated.user = { ...devotee };
        
        // Add to users list if not already present
        if (devotee.id) {
          const existingUserIndex = updated.users?.findIndex(u => u.id === devotee.id) ?? -1;
          
          if (existingUserIndex >= 0) {
            updated.users = [...updated.users];
            updated.users[existingUserIndex] = { ...devotee };
          } else {
            updated.users = [
              { ...devotee },
              ...(updated.users || []).slice(0, 49)
            ];
          }
        }
      }
      
      return updated;
    });
  }, []);

  return (
    <SessionContext.Provider
      value={{
        session,
        setSession, // Add setSession to make it available to components
        updateCart,
        updateUser,
        resetSession,
        resetTimer,
        setUserSelected,
        // Helper to set last origin for navigation context
        setLastOrigin: React.useCallback((origin) => {
          setSession(prev => ({ ...prev, lastOrigin: origin }));
        }, []),
      }}
    >
      {children}
      {showInactiveWarning && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 1000,
          textAlign: 'center',
        }}>
          <h3>Are you still there?</h3>
          <p>Your session will expire soon.</p>
          <button 
            onClick={() => {
              resetTimer();
              setShowInactiveWarning(false);
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px',
            }}
          >
            Continue
          </button>
        </div>
      )}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = React.useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
